import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, FormControl, InputLabel, Select, MenuItem, IconButton } from '@mui/material';
import { Add, Delete, Upload } from '@mui/icons-material';

const AssessmentDialog = ({
  open,
  isEditing,
  newAssessment,
  setNewAssessment,
  selectedAssessment,
  setIsEditingAssessment,
  setSelectedAssessment,
  handleSaveAssessment,
  handleFileUpload,
  fileInputRef,
  setOpenAssessmentDialog,
  colors,
}) => {
  const handleAddQuestion = () => {
    setNewAssessment({
      ...newAssessment,
      questions: [...newAssessment.questions, { question: '', answers: [], correct_answer: '', question_type: 'structured' }],
    });
  };

  const handleRemoveQuestion = (index) => {
    setNewAssessment({
      ...newAssessment,
      questions: newAssessment.questions.filter((_, i) => i !== index),
    });
  };

  const handleQuestionChange = (index, field, value) => {
    setNewAssessment((prev) => {
      const updatedQuestions = [...prev.questions];
      if (field === 'question') {
        updatedQuestions[index] = { ...updatedQuestions[index], question: value || '' };
      } else if (field === 'type') {
        updatedQuestions[index] = {
          ...updatedQuestions[index],
          question_type: value,
          answers: value === 'structured' ? '' : [],
          correct_answer: value === 'structured' ? '' : updatedQuestions[index].correct_answer || '',
        };
      }
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleAnswerChange = (questionIndex, answerIndex, value) => {
    setNewAssessment((prev) => {
      const updatedQuestions = [...prev.questions];
      if (prev.questions[questionIndex].question_type === 'structured') {
        updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], answers: value || '' };
      } else {
        const updatedAnswers = [...updatedQuestions[questionIndex].answers];
        updatedAnswers[answerIndex] = value || '';
        updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], answers: updatedAnswers };
      }
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleCorrectAnswerChange = (questionIndex, value) => {
    setNewAssessment((prev) => {
      const updatedQuestions = [...prev.questions];
      updatedQuestions[questionIndex] = { ...updatedQuestions[questionIndex], correct_answer: value || '' };
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleAddAnswer = (questionIndex) => {
    setNewAssessment((prev) => {
      const updatedQuestions = [...prev.questions];
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        answers: [...updatedQuestions[questionIndex].answers, ''],
      };
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleRemoveAnswer = (questionIndex, answerIndex) => {
    setNewAssessment((prev) => {
      const updatedQuestions = [...prev.questions];
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        answers: updatedQuestions[questionIndex].answers.filter((_, i) => i !== answerIndex),
        correct_answer:
          updatedQuestions[questionIndex].correct_answer ===
          updatedQuestions[questionIndex].answers[answerIndex]
            ? ''
            : updatedQuestions[questionIndex].correct_answer,
      };
      return { ...prev, questions: updatedQuestions };
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpenAssessmentDialog(false);
        setIsEditingAssessment(false);
        setSelectedAssessment(null);
      }}
    >
      <DialogTitle>{isEditing ? 'Edit Assessment' : 'Add Assessment'}</DialogTitle>
      <DialogContent>
        <Box display={'flex'} gap={2}>
          <TextField
            label="Assessment Name"
            fullWidth
            value={newAssessment.name || ''}
            onChange={(e) => setNewAssessment({ ...newAssessment, name: e.target.value })}
            margin="normal"
          />
          <TextField
            label="Assessment Number"
            fullWidth
            type="number"
            value={newAssessment.assess_number || ''}
            onChange={(e) => setNewAssessment({ ...newAssessment, assess_number: e.target.value })}
            margin="normal"
          />
          <TextField
            label="Weight (%)"
            fullWidth
            type="number"
            value={newAssessment.weight || ''}
            onChange={(e) => setNewAssessment({ ...newAssessment, weight: e.target.value })}
            margin="normal"
          />
        </Box>
        <TextField
          label="Description"
          fullWidth
          multiline
          rows={4}
          value={newAssessment.description || ''}
          onChange={(e) => setNewAssessment({ ...newAssessment, description: e.target.value })}
          margin="normal"
        />
        <Box display={'flex'} gap={2}>
          <TextField
            label="Start Date"
            fullWidth
            type="datetime-local"
            value={newAssessment.start_date || ''}
            onChange={(e) => setNewAssessment({ ...newAssessment, start_date: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            fullWidth
            type="datetime-local"
            value={newAssessment.end_date || ''}
            onChange={(e) => setNewAssessment({ ...newAssessment, end_date: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Questions (optional)
        </Typography>
        {newAssessment.questions.map((q, index) => (
          <Box
            key={index}
            sx={{ mb: 2, p: 2, border: '1px solid', borderColor: colors.dark[300], borderRadius: 1 }}
          >
            <TextField
              label={`Question ${index + 1}`}
              fullWidth
              value={q.question || ''}
              onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Question Type</InputLabel>
              <Select
                value={q.question_type || 'structured'}
                onChange={(e) => handleQuestionChange(index, 'type', e.target.value)}
              >
                <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                <MenuItem value="structured">Structured</MenuItem>
              </Select>
            </FormControl>
            {q.question_type === 'structured' ? (
              <TextField
                label="Answer"
                fullWidth
                value={q.answers || ''}
                onChange={(e) => handleAnswerChange(index, 0, e.target.value)}
                margin="normal"
              />
            ) : (
              <Box>
                {q.answers.map((answer, answerIndex) => (
                  <Box key={answerIndex} display="flex" alignItems="center" gap={2} sx={{ mb: 1 }}>
                    <TextField
                      label={`Answer ${answerIndex + 1}`}
                      fullWidth
                      value={answer || ''}
                      onChange={(e) => handleAnswerChange(index, answerIndex, e.target.value)}
                      margin="normal"
                    />
                    {q.answers.length > 1 && (
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveAnswer(index, answerIndex)}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <FormControl fullWidth margin="normal">
                  <InputLabel>Correct Answer</InputLabel>
                  <Select
                    value={q.correct_answer || ''}
                    onChange={(e) => handleCorrectAnswerChange(index, e.target.value)}
                    disabled={q.answers.length === 0}
                  >
                    {q.answers.map((answer, answerIndex) => (
                      <MenuItem key={answerIndex} value={answer}>
                        {answer || `Answer ${answerIndex + 1}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => handleAddAnswer(index)}
                  sx={{ mt: 1 }}
                >
                  Add Answer
                </Button>
              </Box>
            )}
            {newAssessment.questions.length > 1 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => handleRemoveQuestion(index)}
                sx={{ mt: 1 }}
              >
                Remove Question
              </Button>
            )}
          </Box>
        ))}
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddQuestion}
            sx={{ mt: 1 }}
          >
            Add Question
          </Button>
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={() => fileInputRef.current.click()}
            sx={{ mt: 2 }}
          >
            Upload Document
          </Button>
          <input
            type="file"
            hidden
            ref={fileInputRef}
            onChange={(e) => handleFileUpload(e, 'assessment')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setOpenAssessmentDialog(false);
            setIsEditingAssessment(false);
            setSelectedAssessment(null);
          }}
        >
          Cancel
        </Button>
        <Button onClick={handleSaveAssessment} variant="contained">
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssessmentDialog;